import clsx from "clsx";
import { formatDuration, intervalToDuration } from "date-fns";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  Address,
  Asset,
  Balance,
  BalanceAccount,
  THOR,
  TxResult,
  networkConfirmationTime,
  networkExplorerLabel,
  networkLabel,
  networkTxLink,
  signers,
  validateAddress,
} from "rujira.js";
import { useTranslation } from "../../i18n";
import { Animated } from "../icons/Animated";
import { IconDenom } from "../icons/IconDenom";
import { AngleLeft, External } from "../icons/Icons";
import { Input } from "../inputs/Input";
import { LoaderWithContent } from "../loader/LoaderWithContent";
import {
  AssetOverviewModal,
  AssetOverviewModalProps,
  AssetOverviewOption,
} from "./AssetOverviewModal";

import {
  Button,
  Decimal,
  DenomInput,
  Fiat,
  Icons,
  NetworkIcon,
  Radio,
  Toggle,
  Warning,
} from "../..";

import { Select } from "../inputs/Select";
import { TransferTimeRow } from "./TransferTimeRow";

export interface DepositSubmitProps {
  selected?: BalanceAccount;
  amount: bigint;
  target?: string;
  swapMemo?: string;
  onSuccess: (tx: TxResult) => void;
  cancel?: () => void;
  disabled?: boolean;
  isDangerous?: boolean;
  quote: signers.cosmos.QuoteSwap | Error | "loading" | undefined;
  shouldSwap: boolean;
  /** Private Mode: the direction of a Reserve/Spending/public move. */
  isDeposit?: boolean;
  isSubWallet?: boolean;
}

export interface DepositModalProps {
  title?: ReactNode;
  options: (AssetOverviewOption & { balance?: Balance })[];
  overview: Omit<AssetOverviewModalProps, "options" | "onSelect">;
  targets: Address[];
  target?: Address;
  selected?: Balance;
  initialAsset?: Asset;
  Submit: FC<DepositSubmitProps>;
  dismiss: () => void;
  feeWarning?: bigint;
  queryClient: signers.cosmos.QueryClient & signers.cosmos.ThorchainExtension;
  amount?: bigint;
  label?: string | ReactNode;
  inboundAddresses?: { dustThreshold: bigint; chain: string }[];
  actions?: ReactNode;
  renderDetail?: (props: {
    asset: Asset;
    balance?: Balance;
    targets: Address[];
    target?: Address;
    onSuccess: (tx: TxResult) => void;
    cancel: () => void;
    onBack?: () => void;
  }) => ReactNode;
  /** Without one, the modal shows its own receipt (Private Mode does). */
  renderSuccess?: (props: {
    asset: Asset;
    tx: TxResult;
    dismiss: () => void;
  }) => ReactNode;
  isPrivateMode?: boolean;
  setIsDeposit?: (isDeposit: boolean) => void;
  isDeposit?: boolean;
  isSubWallet?: boolean;
  subWalletAddress?: string;
  proxyAddress?: string;
  updateFlagData?: () => void;
}

export const DepositModal: FC<DepositModalProps> = ({
  title,
  options,
  targets,
  selected,
  initialAsset,
  dismiss,
  Submit,
  queryClient,
  amount,
  target,
  label,
  inboundAddresses,
  actions,
  overview,
  renderDetail,
  renderSuccess,
  isPrivateMode = false,
  setIsDeposit = () => undefined,
  isDeposit = true,
  isSubWallet,
  subWalletAddress,
  proxyAddress,
  updateFlagData,
}) => {
  const { t: tCommon } = useTranslation("common");
  const { t: tRedacted } = useTranslation("redacted");
  const [token, setToken] = useState<string | undefined>(
    initialAsset?.metadata.symbol ?? selected?.asset.metadata.symbol
  );
  const [asset, setAsset] = useState<Asset | undefined>(
    initialAsset ?? selected?.asset
  );
  const [tx, setTx] = useState<TxResult>();
  // `initialAsset` can resolve after mount (e.g. a deep link before balances
  // load), so follow it until the user makes their own choice.
  const chosen = useRef(false);

  useEffect(() => {
    if (chosen.current || !initialAsset || initialAsset.asset === asset?.asset)
      return;
    setToken(initialAsset.metadata.symbol);
    setAsset(initialAsset);
  }, [asset?.asset, initialAsset]);

  // Private Mode names the actual move (direction and destination) in every
  // step, whatever title a caller passes.
  const privateDirection = isDeposit ? tCommon("deposit") : tCommon("withdraw");
  const privateDestination = isDeposit
    ? isSubWallet
      ? tRedacted("toSpending")
      : tRedacted("toPrivateAccount")
    : isSubWallet
      ? tRedacted("toPrivateReserve")
      : tRedacted("toPublic");

  if (!token && !asset) {
    return (
      <AssetOverviewModal
        {...overview}
        title={
          isPrivateMode
            ? `${privateDirection} ${privateDestination}`
            : overview.title
        }
        topBar={
          isPrivateMode && !isSubWallet ? (
            <div className="flex jc-e ai-c mb-1">
              <Button
                className="no-shrink"
                onClick={() => setIsDeposit(!isDeposit)}>
                {isDeposit ? tCommon("withdraw") : tCommon("deposit")}
              </Button>
            </div>
          ) : (
            overview.topBar
          )
        }
        options={options}
        onSelect={(selectedAsset) => {
          chosen.current = true;
          setToken(selectedAsset.metadata.symbol);
          setAsset(selectedAsset);
        }}
      />
    );
  }

  const back =
    !selected && !tx
      ? () => {
          chosen.current = true;
          setAsset(undefined);
          setToken(undefined);
        }
      : undefined;

  if (asset && !tx && renderDetail) {
    return renderDetail({
      asset,
      balance:
        selected || options.find((a) => a.asset.asset === asset.asset)?.balance,
      targets,
      target,
      onSuccess: setTx,
      cancel: dismiss,
      onBack: back,
    });
  }

  if (asset && tx && renderSuccess) {
    return renderSuccess({ asset, tx, dismiss });
  }

  return (
    <div className="deposit-modal">
      {(token || asset) && back && (
        <button
          className="transparent color-white hover-primary1 flex ai-c mb-1 fs-14 fw-500 pointer"
          onClick={back}>
          <AngleLeft className="block w-2 h-2 mr-0.5" />
          {tCommon("back")}
        </button>
      )}
      <div className="flex jc-sb ai-c gap-2">
        <h3 className="h3 deposit-modal__heading mb-0">
          <span className="deposit-modal__title">
            {asset && (
              <IconDenom
                denom={asset.metadata.symbol}
                className="w-3 h-3 no-shrink"
              />
            )}
            <span>
              {isPrivateMode ? privateDirection : title || tCommon("deposit")}{" "}
              {asset && asset.metadata.symbol}
            </span>
          </span>
          {(isPrivateMode || asset) && (
            <small className="deposit-modal__destination color-grey fw-400">
              {isPrivateMode ? privateDestination : tCommon("toRujira")}
            </small>
          )}
        </h3>
        {!token && actions}
      </div>
      {label}
      <div className="mt-3">
        {asset && !tx && (
          <>
            <Confirm
              asset={asset}
              amount={amount}
              balance={
                selected ||
                options.find((a) => a.asset.asset === asset.asset)?.balance
              }
              target={target}
              targets={targets}
              Submit={Submit}
              onSuccess={(result) => {
                setTx(result);
                if (isPrivateMode && updateFlagData) {
                  window.setTimeout(updateFlagData, 5_000);
                }
              }}
              cancel={dismiss}
              queryClient={queryClient}
              inboundAddresses={inboundAddresses}
              isPrivateMode={isPrivateMode}
              isDeposit={isDeposit}
              isSubWallet={isSubWallet}
              subWalletAddress={subWalletAddress}
              proxyAddress={proxyAddress}
            />
          </>
        )}
        {tx && (
          <Success
            tx={tx}
            dismiss={dismiss}
            isDeposit={isDeposit}
            isSubWallet={isSubWallet}
          />
        )}
      </div>
    </div>
  );
};

interface DepositConfirmProps {
  asset: Asset;
  balance?: Balance;
  target?: Address;
  targets: Address[];
  Submit: FC<DepositSubmitProps>;
  onSuccess: (tx: TxResult) => void;
  cancel?: () => void;
  queryClient: signers.cosmos.QueryClient & signers.cosmos.ThorchainExtension;
  amount?: bigint;
  inboundAddresses?: { dustThreshold: bigint; chain: string }[];
  isPrivateMode: boolean;
  isDeposit: boolean;
  isSubWallet?: boolean;
  subWalletAddress?: string;
  proxyAddress?: string;
}

const Confirm: FC<DepositConfirmProps> = ({
  asset,
  balance,
  targets,
  target: target_,
  Submit,
  onSuccess,
  cancel,
  queryClient,
  amount: requestAmount,
  inboundAddresses,
  isPrivateMode,
  isDeposit,
  isSubWallet,
  subWalletAddress,
  proxyAddress,
}) => {
  const { t } = useTranslation("common");
  const { t: tRedacted } = useTranslation("redacted");
  const ref = useRef<HTMLInputElement | null>(null);
  const [amount, setAmount] = useState(requestAmount || 0n);
  const [target, setTarget] = useState<string>(target_?.address || "");
  const [custom, setCustom] = useState<string>("");
  const [direct, setDirect] = useState<boolean>(false);
  const [advanced, setAdvanced] = useState<boolean>(false);
  const [hoverAd, setHoverAd] = useState<boolean>(false);
  const [quote, setQuote] = useState<
    undefined | "loading" | signers.cosmos.QuoteSwap | Error
  >();
  const [selectedAccount, setAccount] = useState(balance?.accounts[0]);
  // Balance subscriptions replace these objects during an operation. Select by
  // identity, not reference, and use the latest balance for the selected source.
  const sameAccount = (candidate: BalanceAccount) =>
    candidate.address === selectedAccount?.address &&
    candidate.chain === selectedAccount?.chain &&
    candidate.asset.asset === selectedAccount?.asset.asset;
  const account = balance?.accounts.find(sameAccount) ?? selectedAccount;
  const dustThreshold = inboundAddresses?.find(
    (a) => a.chain === account?.asset.chain
  )?.dustThreshold;

  useEffect(() => {
    if (ref.current && target === "custom") {
      ref.current.focus();
    }
  }, [target, ref]);

  // Reserve/Spending movements use THORChain balances directly, not a native
  // asset bridge or swap. Keep bridge quoting exclusive to public deposits.
  const canSwap =
    !isPrivateMode &&
    !!account &&
    (asset.chain !== account.asset.chain ||
      asset.metadata.symbol !== account.asset.metadata.symbol);

  const shouldSwap = canSwap && !direct;
  const standardChain = networkLabel(asset.chain);
  const directChain = account?.asset ? networkLabel(account.asset.chain) : "";
  const directAsset = account?.asset.metadata.symbol || asset.metadata.symbol;
  const directAssetChain = account?.asset.chain;
  const nonStandardDepositWarning = direct
    ? t("common:directDepositWarning", {
        asset: directAsset,
        standardChain,
        directChain,
      })
    : t("common:nonStandardDepositWarning", {
        asset: directAsset,
        standardChain,
        directChain,
      });

  // Determine the actual destination address
  const destination = target === "custom" ? custom : target;

  const isCustomAddressInvalid =
    target === "custom" && custom !== "" && !validateAddress(THOR, custom);

  // Fetch swap quote when conditions are met
  useEffect(() => {
    setQuote(undefined);
    if (!account) return;
    if (!shouldSwap) return;
    if (!amount) return;
    if (direct) return;
    setQuote("loading");
    queryClient.thorchain
      .getSwapQuote({
        fromAsset: account.asset.asset,
        toAsset: asset.asset,
        amount: amount,
        streamingInterval: 1n,
        streamingQuantity: 0n,
        destination,
      })
      .then(setQuote)
      .catch(setQuote);
  }, [account, amount, destination, asset, direct]);

  const depositAmount =
    typeof quote === "object" && "memo" in quote
      ? quote.expectedAmountOut
      : amount;

  const depositFee =
    (typeof quote === "object" && "memo" in quote && quote.fees?.total) || 0n;

  const price = asset.price?.current || 0n;
  const feeValue = price ? (price * depositFee) / 10n ** 12n : 0n;
  const receivedValue = price ? (price * depositAmount) / 10n ** 12n : 0n;

  return (
    <>
      {balance && balance.accounts.length > 0 ? (
        <>
          <h2 className="fs-14 fw-500 color-grey mb-1 ml-1">
            {t("common:selectSourceAccount")}
          </h2>
          <div className="br-1 p-2 bg-black mb-2 flex dir-c gap-y-1">
            {balance.accounts.map((x) => (
              <div
                key={x.address + x.asset.chain}
                onClick={() => {
                  setAccount(x);
                }}
                className={clsx({
                  "flex ai-c fs-14 condensed pointer": true,
                  "color-grey": !sameAccount(x),
                  "color-white": sameAccount(x),
                })}>
                <Radio checked={sameAccount(x)} readOnly />
                {isPrivateMode &&
                (x.chain === "STEALTH" || (isSubWallet && !isDeposit)) ? (
                  <span className="mx-1">
                    {tRedacted(
                      x.chain === "STEALTH"
                        ? isDeposit && !isSubWallet
                          ? "privateAccount"
                          : "privateReserve"
                        : "spending"
                    )}
                  </span>
                ) : (
                  <div
                    data-tooltip-content={x.address}
                    data-tooltip-id="modal-tip"
                    onClick={() => {
                      toast.success("Address copied to clipboard");
                      navigator.clipboard.writeText(x.address);
                    }}
                    className="inconsolata mx-1">
                    {x.address.substring(0, 6) + "…" + x.address.slice(-6)}
                  </div>
                )}
                <div className="flex ai-c gap-0.5">
                  <NetworkIcon
                    network={isPrivateMode ? THOR : x.asset.chain}
                    className="w-2 h-2"
                  />
                  <span className="">
                    {networkLabel(isPrivateMode ? THOR : x.asset.chain)}
                  </span>
                </div>
                <div className="flex dir-r ai-e ml-a">
                  <div
                    className={clsx({
                      "tag tag--md ml-a": true,
                      "tag--teal": sameAccount(x),
                    })}>
                    <Decimal
                      amount={x.balance}
                      symbol={x.asset.metadata.symbol}
                      className="color-grey"
                      symbolClassName="color-grey condensed fw-500"
                    />
                  </div>
                  {x.valueUsd ? (
                    <div
                      className="iflex ml-0.5 ml-sm-1 fs-13 color-white"
                      style={{ marginBottom: "0.05rem" }}>
                      (
                      <Fiat
                        amount={x.valueUsd}
                        symbol="$"
                        padSymbol={false}
                        decimals={8}
                        className="fs-12 color-white"
                      />
                      )
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : isPrivateMode ? (
        <p className="color-grey text-center my-2">
          {tRedacted("noSourceBalance", { asset: asset.metadata.symbol })}
        </p>
      ) : null}

      <DenomInput
        className="mt-2.5 bg-black"
        symbol={asset.metadata.symbol}
        amount={amount}
        decimals={8}
        onChangeAmount={setAmount}
        max={BigInt(account?.balance || 0)}
        maxLabel={`${t("common:balance")}:`}
        fiat={{ price, symbol: "$" }}
      />

      <div className="row wrap mt-1.5 mt-sm-2 condensed fs-14 px-2 gap-y-1">
        {!isPrivateMode && (
          <>
            <div
              className={clsx(
                "col-12 col-xs-4 text-center text-xs-left fw-500",
                amount > 0n && amount < (dustThreshold ?? 0n)
                  ? "color-red"
                  : "color-grey"
              )}>
              {t("common:minimumDeposit")}
            </div>
            <div className="col-12 col-xs-8 text-center text-xs-right color-grey">
              {(dustThreshold ?? 0n) > 0n && (
                <Decimal
                  amount={dustThreshold!}
                  symbol={account?.asset.metadata.symbol}
                  className={clsx("pointer", {
                    "color-red": amount > 0n && amount < dustThreshold!,
                    "color-grey": amount > 0n && amount > dustThreshold!,
                  })}
                  symbolClassName={clsx("fs-14 condensed fw-500", {
                    "color-red": amount > 0n && amount < dustThreshold!,
                    "color-grey": amount > 0n && amount > dustThreshold!,
                  })}
                  onClick={() => setAmount(dustThreshold! ?? 0n)}
                />
              )}
            </div>

            <TransferTimeRow
              network={account?.asset.chain}
              label={t("common:depositTime")}
              tooltip={t("common:depositTimeTooltip")}
              labelClassName="col-12 col-xs-4 text-center text-xs-left color-grey fw-500"
              valueClassName="col-12 col-xs-8 text-center text-xs-right"
            />

            <div className="col-12 col-xs-4 text-center text-xs-left color-grey fw-500">
              {t("common:swapFee")}
            </div>
            <div className="col-12 col-xs-8 text-center text-xs-right color-teal">
              <LoaderWithContent
                loading={quote === "loading"}
                content={
                  <div className="color-grey">
                    <Decimal
                      amount={depositFee}
                      symbol={
                        direct
                          ? account?.asset.metadata.symbol
                          : asset.metadata.symbol
                      }
                      className={
                        direct || !shouldSwap ? "color-grey" : undefined
                      }
                      symbolClassName="color-grey fs-14 condensed fw-500"
                    />
                    <div className="iflex ml-1 fs-13 color-grey">
                      (
                      <Fiat
                        amount={feeValue ?? 0n}
                        symbol="$"
                        padSymbol={false}
                        decimals={8}
                        className="numeric-input__value color-grey fs-12"
                      />
                      )
                    </div>
                  </div>
                }
              />
            </div>

            <div className="col-12 col-xs-4 text-center text-xs-left color-grey fw-500">
              {t("common:receivedAmount")}
            </div>
            <div className="col-12 col-xs-8 text-center text-xs-right color-teal">
              <LoaderWithContent
                loading={quote === "loading"}
                content={
                  <div className="color-white">
                    <Decimal
                      amount={depositAmount}
                      symbol={
                        direct
                          ? account?.asset.metadata.symbol
                          : asset.metadata.symbol
                      }
                      symbolClassName="color-grey fs-14 condensed fw-500"
                    />
                    <div className="iflex ml-1 fs-13 color-grey">
                      (
                      <Fiat
                        amount={receivedValue ?? 0n}
                        symbol="$"
                        padSymbol={false}
                        decimals={8}
                        className="numeric-input__value color-grey fs-12"
                      />
                      )
                    </div>
                  </div>
                }
              />
            </div>
          </>
        )}

        <div
          className="flex w-full jc-c ai-c gap-0.5 pointer color-grey hover-white"
          onMouseEnter={() => setHoverAd(true)}
          onMouseLeave={() => setHoverAd(false)}
          onClick={() => setAdvanced((prev) => !prev)}>
          <Animated
            name="filters"
            paused={!hoverAd}
            className={clsx({
              "w-2 h-2": true,
              "color-grey": !hoverAd,
              "color-white": hoverAd,
            })}
          />
          {advanced
            ? t("common:hideAdvancedOptions")
            : t("common:showAdvancedOptions")}
        </div>
      </div>

      {/* {quote === "loading" && <Loader className="w-2 h-2" />} */}

      {advanced && (
        <div className="br-1 p-2 bg-black mt-1 row wrap condensed fs-14 px-2 gap-y-1">
          <div className="col-12 col-xs-4 text-center text-xs-left color-grey fw-500">
            {t("common:destination")}
          </div>
          {isPrivateMode ? (
            <div className="col-12 col-xs-8 text-center text-xs-right overflow-hidden nowrap">
              {isDeposit
                ? isSubWallet
                  ? subWalletAddress
                  : proxyAddress
                : isSubWallet
                  ? proxyAddress
                  : target}
            </div>
          ) : (
            <div className="col-12 col-xs-8 text-center text-xs-right">
              <Select
                containerClassName="fs-14"
                className="select--inline text-right"
                value={target}
                onChange={(e) => setTarget(e.currentTarget.value)}>
                {targets.map((x) => (
                  <option
                    key={x.address}
                    value={x.address}
                    className={"bg-darkGrey"}>
                    {x.address}
                  </option>
                ))}
                <option className={"bg-darkGrey"} value="custom">
                  {t("common:customAddress")}
                </option>
              </Select>
            </div>
          )}
          {!isPrivateMode && target === "custom" && (
            <>
              <div className="col-12 col-xs-4 text-center text-xs-left color-grey fw-500">
                {t("common:customDestination")}
              </div>
              <div className="col-12 col-xs-8 text-center text-xs-right">
                <Input
                  containerClassName="fs-14"
                  value={custom}
                  onChange={(e) => setCustom(e.currentTarget.value)}
                  className="input--inline text-right"
                  innerRef={ref}
                />
              </div>
            </>
          )}
          {canSwap && (
            <>
              {asset.chain === THOR ? null : (
                <div className="col-12 text-center text-xs-right">
                  <Toggle
                    className="toggle--xs"
                    checked={direct}
                    onChange={() => setDirect((prev) => !prev)}>
                    <span className="toggle__label">
                      {t("common:depositAssetOnChain")} {directAsset}
                      {directAssetChain && (
                        <span className="color-grey">.{directAssetChain}</span>
                      )}
                    </span>
                  </Toggle>
                </div>
              )}
              {asset.chain !== THOR && (
                <Warning
                  className="warning--sm condensed flex ai-c mt-1"
                  color="orange">
                  <Animated
                    name="swap"
                    className="color-orange block no-shrink"
                    style={{ width: "2.5rem", height: "2.5rem" }}
                  />
                  <small
                    className="text-left fs-14"
                    dangerouslySetInnerHTML={{
                      __html: nonStandardDepositWarning,
                    }}
                  />
                </Warning>
              )}
            </>
          )}
          {isCustomAddressInvalid && (
            <div className="flex dir-r jc-c grow mt-1">
              <Warning color="red" className="condensed iflex grow">
                <Icons.ExclamationTriangle className="color-red" />
                <span className="warning__msg">
                  {t("common:invalidThorAddress")}
                </span>
              </Warning>
            </div>
          )}
        </div>
      )}
      <Submit
        selected={account}
        amount={amount}
        target={target !== "custom" ? target : custom}
        swapMemo={"memo"}
        onSuccess={onSuccess}
        cancel={cancel}
        disabled={isCustomAddressInvalid}
        isDangerous={false}
        quote={quote}
        shouldSwap={shouldSwap}
        isDeposit={isDeposit}
        isSubWallet={isSubWallet}
      />
    </>
  );
};

const Success: FC<{
  tx: TxResult;
  dismiss: () => void;
  isDeposit: boolean;
  isSubWallet?: boolean;
}> = ({ tx, dismiss, isDeposit, isSubWallet }) => {
  const { t: tCommon } = useTranslation("common");
  const { t: tRedacted } = useTranslation("redacted");
  return (
    <>
      <div className="mt-2 flex ai-c br-2 bg-teal-10 p-2">
        <Animated
          name="deposited"
          className="color-teal block w-6 h-a no-shrink"
        />
        <span className="color-teal fs-18 fw-600 mx-1">
          {isDeposit
            ? isSubWallet
              ? tRedacted("depositToSpending")
              : tCommon("depositStarted")
            : tRedacted("withdrawSuccessful")}
        </span>
      </div>
      <div className="table table--no-hover mt-1">
        <tbody>
          <tr>
            <td className="color-grey">
              {networkLabel(tx.network)}{" "}
              {isDeposit ? tCommon("depositEta") : tRedacted("withdrawEta")}
            </td>
            <td>
              {formatDuration(
                intervalToDuration({
                  start: 0,
                  end: networkConfirmationTime(tx.network),
                }),
                { format: ["hours", "minutes", "seconds"], zero: false }
              )}
            </td>
          </tr>
          {/* Only a deposit from the public wallet is the user's own public
              transaction. An explorer visit for a Reserve or Spending move
              would hand it the Spending address and the user's IP. */}
          {isDeposit && !isSubWallet && (
            <>
              <tr>
                <td className="color-grey">{tCommon("viewTx")}</td>
                <td>
                  <a
                    href={networkTxLink(tx)}
                    target="_blank"
                    className="color-white hover-primary1 no-underline">
                    {networkExplorerLabel(tx.network)}
                    <External className="inline-block w-1.5 h-1.5 ml-0.5" />
                  </a>
                </td>
              </tr>
              <tr>
                <td className="color-grey">{tCommon("trackDeposit")}</td>
                <td>
                  <a
                    href={networkTxLink({
                      network: THOR,
                      txHash: tx.txHash.replace(/^0x/, "").toUpperCase(),
                    })}
                    target="_blank"
                    className="color-white hover-primary1 no-underline">
                    THORChain.net
                    <External className="inline-block w-1.5 h-1.5 ml-0.5" />
                  </a>
                </td>
              </tr>
            </>
          )}
        </tbody>
      </div>
      <div className="modal__footer p-3 text-right">
        <Button label="Close" onClick={dismiss} />
      </div>
    </>
  );
};
